import NavContractIcon from "@/assets/nav-contract.svg";
import NavAccountIcon from "@/assets/nav-account.svg";
import NavSupportIcon from "@/assets/nav-support.svg";
import NavMonitorIcon from "@/assets/nav-monitor.svg";
import { FaUserAlt, FaStar, FaLock, FaUsers } from "react-icons/fa";
import { AiFillDollarCircle } from "react-icons/ai";
import { PlanData } from "@/types";
import { Link } from "react-router-dom";

// Environment configuration.
// Production: values injected into window.__ENV__ by entrypoint.sh (envsubst).
// Development: values loaded from .env.local by Vite (VITE_* prefix).
// When running locally without envsubst, window.__ENV__ contains unresolved
// "${...}" placeholders, so we detect that and fall back to Vite env.
const runtimeEnv = window.__ENV__;
const isResolved = runtimeEnv?.API_URL && !runtimeEnv.API_URL.startsWith("$");
const env = isResolved
  ? runtimeEnv
  : {
      API_URL: import.meta.env.VITE_API_URL,
      DOMAIN_URL: import.meta.env.VITE_DOMAIN_URL,
      GITHUB_OAUTH_CLIENT_ID: import.meta.env.VITE_GITHUB_OAUTH_CLIENT_ID,
      TELEGRAM_BOT: import.meta.env.VITE_TELEGRAM_BOT,
    };

// API server.
export const API_VERSION = "v1";
export const API_URL = env.API_URL;
// Domain URL.
export const DOMAIN_URL = env.DOMAIN_URL;
export const WS_URL = API_URL?.replace("http", "ws");

// GitHub.
const DEV_CLIENT_ID = "4a12920a28e83a3a0e93"; // "sec3-crtest" in GitHub OAuth Apps (sec3-product)
export const GITHUB_OAUTH_CLIENT_ID =
  env.GITHUB_OAUTH_CLIENT_ID || DEV_CLIENT_ID;
export const GITHUB_OAUTH_API = `https://github.com/login/oauth/authorize?client_id=${GITHUB_OAUTH_CLIENT_ID}&scope=user:email%20read:user`;

// Slack.
export const SLACK_REDIRECT_URI = `${DOMAIN_URL}/add_slack`;
// Telegram.
export const TELEGRAM_BOT = env.TELEGRAM_BOT;

export const DEMO_PROGRAM_ADDRESS_DEVNET =
  "zzEPkxAU9v1QEFGj23FBErhWSLdXJcZpBXTaMG8Vp1o";

export const TASK_PAGE_SIZE = 10;
export const ALERTS_PAGE_SIZE = 10;
export const BOTS_PAGE_SIZE = 10;
export const BASIC_PRICE_MONTHLY = 99;
export const BUILD_PRICE_MONTHLY = 1999;
export const SCALE_PRICE_MONTHLY = 9999;
export const BASIC_PRICE_YEARLY = 999;
export const BUILD_PRICE_YEARLY = 19999;
export const SCALE_PRICE_YEARLY = 99999;
export const UPLOAD_FILE_LIMIT = 25 * 1024 * 1024; // 25MB

export const NAV_DATA = [
  {
    label: "X-ray Scanner",
    path: "/task",
    icon: NavContractIcon,
  },
  {
    label: "WatchTower",
    path: "/alerts",
    icon: NavMonitorIcon,
  },
  {
    label: "Account",
    path: "/account",
    icon: NavAccountIcon,
  },
  {
    label: "Docs",
    path: "#",
    icon: NavAccountIcon,
  },
  {
    label: "Support",
    path: "#",
    icon: NavSupportIcon,
  },
  {
    label: "Admin",
    path: "/admin",
    icon: NavAccountIcon,
  },
  {
    label: "Launch Audit",
    path: "#",
    icon: NavContractIcon,
  },
];

export const MONITOR_TAB_DATA = [
  {
    value: "bots",
    label: "Bots",
  },
  {
    value: "alerts",
    label: "Alerts",
  },
  {
    value: "insights",
    label: "Insights",
  },
];

export const ACCOUNT_NAV: { label: string; icon: any }[] = [
  {
    label: "Details",
    icon: <FaUserAlt size={20} />,
  },
  {
    label: "Team",
    icon: <FaUsers size={20} />,
  },
  {
    label: "Plan",
    icon: <FaStar size={20} />,
  },
  {
    label: "Billing",
    icon: <AiFillDollarCircle size={20} />,
  },
  {
    label: "Security",
    icon: <FaLock size={20} />,
  },
];
export const COUNTRIES = [
  {
    id: "United States",
    title: "United States",
  },
  {
    id: "Canada",
    title: "Canada",
  },
  {
    id: "United Kingdom",
    title: "United Kingdom",
  },
  {
    id: "Germany",
    title: "Germany",
  },
  {
    id: "France",
    title: "France",
  },
  {
    id: "Italy",
    title: "Italy",
  },
  {
    id: "Spain",
    title: "Spain",
  },
  {
    id: "Japan",
    title: "Japan",
  },
  {
    id: "Belgium",
    title: "Belgium",
  },
];

export const BOT_NOTIFICATION_DESTINATIONS = [
  {
    id: "email",
    title: "Email",
  },
  {
    id: "sms",
    title: "SMS",
  },
  {
    id: "slack",
    title: "Slack",
  },
  {
    id: "telegram",
    title: "Telegram",
  },
  {
    id: "discord",
    title: "Discord",
  },
  {
    id: "webhook",
    title: "Webhook",
  },
];

export const BUILD_PLAN_TEAM_SIZE_LIMIT = 5;
export const SCALE_PLAN_TEAM_SIZE_LIMIT = 10;

export const ONBOARDING_TITLE = "Login to your Sec3 Pro Account";
export const ONBOARDING_SUBTITLE =
  "Login for Sec3 Pro X-ray Security Scanner and WatchTower Security Monitor";
export const ONBOARDING_TEXT =
  "Sec3 Pro provides advanced security tools to protect web3 protocols. Led by in depth research and analysis, our goal is to make web3 development as seamless and secure as possible.";

export const PLAN_DATA: PlanData[] = [
  {
    name: "Basic",
    description: " ",
    monthlyPrice: BASIC_PRICE_MONTHLY,
    yearlyPrice: BASIC_PRICE_YEARLY,
    isPopular: false,
    features: [
      <div>
        Cover{" "}
        <Link to="/sves">
          <u>10+ out of 50+ SVEs</u>
        </Link>
      </div>,
      "10 Scan Credits/month",
      "GitHub CI Action",
      "Dashboard",
    ],
  },
  {
    name: "Build",
    description: "Best for small business",
    monthlyPrice: BUILD_PRICE_MONTHLY,
    yearlyPrice: BUILD_PRICE_YEARLY,
    isPopular: true,
    features: [
      <div>
        Cover{" "}
        <Link to="/sves">
          <u>50+ SVEs</u>
        </Link>
      </div>,
      "100 Scan Credits/month",
      "10 Certificates/month",
      "Dashboard",
      "24h Support Response",
      "GitHub CI Action",
      "Beta Features",
      "New Vulnerabilities Alert",
    ],
  },
  {
    name: "Scale",
    description: " ",
    monthlyPrice: SCALE_PRICE_MONTHLY,
    yearlyPrice: SCALE_PRICE_YEARLY,
    isPopular: false,
    features: [
      <div>
        Cover{" "}
        <Link to="/sves">
          <u>50+ SVEs</u>
        </Link>
      </div>,
      "1000 Scan Credits/month",
      "100 Certificates/month",
      "Dashboard",
      "12h Support Response",
      "GitHub CI Action",
      "Beta Features",
      "New Vulnerabilities Alert",
    ],
  },
  {
    name: "Diamond",
    description: " ",
    monthlyPrice: 0,
    yearlyPrice: 0,
    isPopular: false,
    features: [
      "Full Retainer Audit",
      "Unlimited Scans",
      "Unlimited Certificates",
      "Dashboard",
      "Streamlining Support",
      "GitHub CI Action",
      "Beta Features",
      "New Vulnerabilities Alert",
    ],
  },
];

export const SUPPORTED_CHAIN_DATA: {
  id: string;
  title: string;
  mobileTitle: string;
  enabled: boolean;
  deprecated?: boolean;
}[] = [
  {
    id: "Solana",
    title: "Solana",
    mobileTitle: "Solana",
    enabled: true,
  },
  {
    id: "Ethereum",
    title: "Ethereum",
    mobileTitle: "Ethereum",
    enabled: false,
  },
  {
    id: "Polygon zkEVM",
    title: "Polygon zkEVM",
    mobileTitle: "Polygon zkEVM",
    enabled: false,
    deprecated: true,
  },
];

export const CHAIN_DATA = SUPPORTED_CHAIN_DATA.filter(
  ({ deprecated }) => !deprecated,
);

export const NETWORK_DATA: {
  [key: string]: {
    id: string;
    title: string;
    mobileTitle: string;
    enabled: boolean;
  }[];
} = {
  Ethereum: [
    {
      id: "Mainnet",
      title: "Mainnet",
      mobileTitle: "Mainnet",
      enabled: true,
    },
  ],
  "Polygon zkEVM": [
    {
      id: "Mainnet",
      title: "Mainnet",
      mobileTitle: "Mainnet",
      enabled: true,
    },
    {
      id: "Testnet",
      title: "Testnet",
      mobileTitle: "Testnet",
      enabled: false,
    },
  ],
  Solana: [
    {
      id: "Mainnet-beta",
      title: "Mainnet-beta",
      mobileTitle: "Mainnet",
      enabled: true,
    },
    {
      id: "Devnet",
      title: "Devnet",
      mobileTitle: "Devnet",
      enabled: true,
    },
  ],
};

export const SEVERITY_MENU_DATA = [
  {
    id: "",
    title: "All",
  },
  {
    id: "low",
    title: "Low",
  },
  {
    id: "medium",
    title: "Medium",
  },
  {
    id: "high",
    title: "High",
  },
  {
    id: "critical",
    title: "Critical",
  },
];

export const SEVERITY_COLORS = {
  critical: "#6A120F",
  high: "#F35C4B",
  medium: "#F79C05",
  low: "#6DC484",
};

export const TYPE_COLORS = [
  "#6DC484",
  "#5366E8",
  "#7A00CE",
  "#DF2F2B",
  "#FFE607",
  "#525252",
];

export const TABLE_PAGINATION = [
  {
    id: 10,
    title: "10 / page",
  },
  {
    id: 20,
    title: "20 / page",
  },
  {
    id: 50,
    title: "50 / page",
  },
  {
    id: 100,
    title: "100 / page",
  },
];

export const FIGURE_TIME_RANGE_DATA = [
  {
    id: "1 hours",
    title: "Last Hour",
    value: "1 hours",
  },
  {
    id: "12 hours",
    title: "Last 12 Hours",
    value: "12 hours",
  },
  {
    id: "24 hours",
    title: "Last 24 Hours",
    value: "24 hours",
  },
  {
    id: "7 days",
    title: "Last Week",
    value: "7 days",
  },
  {
    id: "1 months",
    title: "Last Month",
    value: "1 months",
  },
  {
    id: "3 months",
    title: "Last 3 Months",
    value: "3 months",
  },
  {
    id: "6 months",
    title: "Last 6 Months",
    value: "6 months",
  },
];
export const FIGURE_INTERVAL_DATA = [
  {
    id: "1m",
    title: "Minute",
    value: "1m",
  },
  {
    id: "1h",
    title: "Hour",
    value: "1h",
  },
  {
    id: "1d",
    title: "Day",
    value: "1d",
  },
  {
    id: "7d",
    title: "Week",
    value: "7d",
  },
];
export const FIGURE_CHART_TYPE_DATA = [
  {
    id: "line",
    title: "Line Chart",
    value: "line",
  },
  {
    id: "bar",
    title: "Bar Chart",
    value: "bar",
  },
  {
    id: "pie",
    title: "Pie Chart",
    value: "pie",
  },
  {
    id: "area",
    title: "Area Chart",
    value: "area",
  },
  {
    id: "table",
    title: "Table",
    value: "table",
  },
  {
    id: "value",
    title: "Value",
    value: "value",
  },
];

export const INSIGHT_LAYOUT_COL_NUM = 12;

export const TOKEN_METADATA_DEVNET =
  "metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s";
export const DEMO_METAPLEX_PROGRAM_ADDRESS_DEVNET =
  "8RYczyB7MvyZHeVgQ9MQes66PJazoPqR4BADkedAuK5p";
